/**
 * Admin - Cue Snapshot API
 *
 * Rebuild and monitor the Cue Daytona snapshot — a fixed-name primary +
 * standby pair, or one content-named snapshot kept current by the reconciler,
 * depending on `DAYTONA_SNAPSHOT_RECONCILE_ENABLED` (the status's `strategy`).
 *
 * Base path: /api/admin/cue
 *
 * Auth: PBAC — `admin:cue` read (status) / write (rebuild).
 *
 * ## No credential material crosses this boundary
 *
 * The status response names snapshots and reports availability. `skillsUrl` is
 * the resolved `CUE_SKILLS_URL` — a canonical, unsigned S3 object URL (no query
 * string, no `X-Amz-Signature`), i.e. a location rather than a bearer token, and
 * one the admin panel already renders. Nothing here is credential material, so
 * the routes carry no `sensitivity` marker.
 */

import { z } from "zod";

import {
  CueRebuildModeSchema,
  CueRebuildStatusSchema
} from "../../../shared/admin/cue-rebuild-status.schema";
import { ApiSuccessSchema } from "../../common/response.schema";

// ============================================================
// Schemas
// ============================================================

/**
 * Optional rebuild scope. Omitted means `"full"`.
 *
 * Under the `standby-pair` strategy, `"primary"` requires the standby to be
 * available — the controller answers HTTP 409 (`reason: "standby_unavailable"`)
 * otherwise, so the primary's dark window is never opened without a fallback.
 *
 * Under the `reconciled` strategy there is one snapshot and no standby, so only
 * `"full"` means anything; `"primary"` and `"standby"` answer HTTP 409
 * (`reason: "standby_pair_retired"`) and schedule nothing. A `"full"` rebuild
 * there is refused too, with a `message` naming why, while the
 * `daytona.snapshots.reconcile` admin switch is off — HTTP 409
 * (`reason: "reconcile_switched_off"`) — or cannot be read — HTTP 503
 * (`reason: "reconcile_switch_unread"`), the one refusal a retry can clear.
 *
 * Every refusal is an error response carrying `scheduled: false`, and none is
 * part of the success contract below.
 */
export const CueRebuildSnapshotBodySchema = z.object({
  mode: CueRebuildModeSchema.optional()
});

/**
 * Result of scheduling a rebuild. The rebuild itself runs in the background;
 * `scheduled` only reports that it was queued — under `reconciled`, that a
 * reconcile pass for the Cue snapshot was kicked.
 *
 * `alreadyRunning` is per-pod and in-memory — a rebuild or kick in flight on
 * another pod reads as `false` here and is resolved by the cross-pod Redis lock.
 */
export const CueRebuildSnapshotResultSchema = z.object({
  scheduled: z.boolean(),
  alreadyRunning: z.boolean()
});

// ============================================================
// ZAdminCue - Endpoint Definitions
// ============================================================

export const ZAdminCue = {
  /**
   * Cross-pod snapshot rebuild status.
   * GET /api/admin/cue/snapshot/status
   */
  GetSnapshotStatus: {
    method: "GET" as const,
    path: "/api/admin/cue/snapshot/status",
    Response: ApiSuccessSchema(CueRebuildStatusSchema)
  },

  /**
   * Re-trigger a snapshot rebuild. Returns as soon as it is queued.
   * POST /api/admin/cue/snapshot/rebuild
   */
  RebuildSnapshot: {
    method: "POST" as const,
    path: "/api/admin/cue/snapshot/rebuild",
    Body: CueRebuildSnapshotBodySchema,
    Response: ApiSuccessSchema(CueRebuildSnapshotResultSchema)
  }
} as const;

// ============================================================
// Convenience Type Exports
// ============================================================

export type CueRebuildSnapshotBody = z.infer<typeof CueRebuildSnapshotBodySchema>;
export type CueRebuildSnapshotResult = z.infer<typeof CueRebuildSnapshotResultSchema>;
